/**
 * beb.ts — Categoría "Bebidas" (10 productos demo).
 */
import { buildProduct } from "./_factory";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_beb",
  skuPrefix: "BEB",
  categoria: "Bebidas",
} as const;

export const cat_beb_products: Producto[] = [
  buildProduct({
    ...CAT,
    index: 0,
    nombre: "Agua con Gas Manantial 600ml",
    marca: "Manantial",
    modelo: "Gas",
    origen: "Colombia",
    costo: 1200,
    precio: 2500,
    stock: 180,
    stockMinimo: 40,
    stockMaximo: 300,
    unidad: "botella",
    ubicacionFisica: "Nevera 3, bebidas frías",
    proveedor: "Postobón S.A.",
    vencimientoMeses: 10,
    pesoNeto: "600ml",
    descripcionLarga:
      "Agua mineral carbonatada natural, burbuja fina y persistente. Refrescante, sin calorías, ideal como acompañante o para cocteles.",
    especificaciones: [
      { clave: "Gas", valor: "Natural" },
      { clave: "Envase", valor: "PET reciclable" },
    ],
    imageKeywords: ["sparkling-water", "agua-gas", "bottle"],
    color: "bg-sky-400",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 1,
    nombre: "Coca-Cola 350ml",
    marca: "Coca-Cola",
    modelo: "Original",
    origen: "Colombia",
    costo: 1800,
    precio: 3500,
    stock: 220,
    stockMinimo: 50,
    stockMaximo: 400,
    unidad: "lata",
    ubicacionFisica: "Nevera 3, bebidas frías",
    proveedor: "Coca-Cola FEMSA",
    vencimientoMeses: 10,
    pesoNeto: "350ml",
    descripcionLarga:
      "Coca-Cola Original 350ml, la fórmula clásica mundial. Dulce, refrescante y burbujeante. Ideal para acompañar comidas rápidas.",
    especificaciones: [
      { clave: "Envase", valor: "Lata aluminio" },
      { clave: "Azúcar", valor: "39g" },
    ],
    imageKeywords: ["coca-cola", "cola", "soda-can"],
    color: "bg-red-600",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 2,
    nombre: "Cerveza Club Colombia 330ml",
    marca: "Club Colombia",
    modelo: "Dorada",
    origen: "Colombia",
    costo: 3200,
    precio: 5500,
    stock: 150,
    stockMinimo: 35,
    stockMaximo: 280,
    unidad: "botella",
    ubicacionFisica: "Nevera 4, cervezas",
    proveedor: "Bavaria S.A.",
    vencimientoMeses: 8,
    pesoNeto: "330ml",
    tieneIVA: true,
    porcentajeIVA: 19,
    descripcionLarga:
      "Cerveza Pilsner premium, sabor equilibrado con notas de malta tostada. Ganadora de medallas internacionales. 4.7% alcohol.",
    especificaciones: [
      { clave: "Estilo", valor: "Pilsner" },
      { clave: "Alcohol", valor: "4.7%" },
    ],
    imageKeywords: ["beer", "cerveza", "bottled-beer"],
    color: "bg-amber-600",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 3,
    nombre: "Jugo Hit Naranja 200ml",
    marca: "Postobón",
    modelo: "Hit Naranja",
    origen: "Colombia",
    costo: 1400,
    precio: 2500,
    stock: 200,
    stockMinimo: 45,
    stockMaximo: 360,
    unidad: "tetrapak",
    ubicacionFisica: "Estante B1, jugos",
    proveedor: "Postobón S.A.",
    vencimientoMeses: 10,
    pesoNeto: "200ml",
    descripcionLarga:
      "Jugo de naranja 12% fruta, con vitaminas A, C y D. Empaque Tetra Pak que conserva el sabor sin refrigerar.",
    especificaciones: [
      { clave: "Fruta", valor: "12%" },
      { clave: "Vitaminas", valor: "A, C, D" },
    ],
    imageKeywords: ["orange-juice", "jugo-naranja", "juice-box"],
    color: "bg-orange-500",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 4,
    nombre: "Red Bull 250ml",
    marca: "Red Bull",
    modelo: "Energy Drink",
    origen: "Austria",
    costo: 5800,
    precio: 8900,
    stock: 100,
    stockMinimo: 22,
    stockMaximo: 180,
    unidad: "lata",
    ubicacionFisica: "Nevera 3, bebidas frías",
    proveedor: "Red Bull Colombia",
    vencimientoMeses: 12,
    pesoNeto: "250ml",
    descripcionLarga:
      "Bebida energizante clásica con cafeína, taurina y vitaminas del grupo B. Rendimiento mental y físico inmediato.",
    especificaciones: [
      { clave: "Cafeína", valor: "80mg" },
      { clave: "Formato", valor: "Lata 250ml" },
    ],
    imageKeywords: ["red-bull", "energy-drink", "energetica"],
    color: "bg-blue-600",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 5,
    nombre: "Té Hatsu Mora 400ml",
    marca: "Hatsu",
    modelo: "Mora Negra",
    origen: "Colombia",
    costo: 2800,
    precio: 4500,
    stock: 130,
    stockMinimo: 28,
    stockMaximo: 240,
    unidad: "botella",
    ubicacionFisica: "Nevera 3, bebidas frías",
    proveedor: "Postobón S.A.",
    vencimientoMeses: 10,
    pesoNeto: "400ml",
    descripcionLarga:
      "Té negro premium con mora, antioxidantes naturales. Endulzado con stevia, bajo en calorías. Sabor único y refrescante.",
    especificaciones: [
      { clave: "Tipo", valor: "Té negro" },
      { clave: "Azúcar", valor: "Stevia" },
    ],
    imageKeywords: ["iced-tea", "te-mora", "tea-bottle"],
    color: "bg-purple-700",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 6,
    nombre: "Agua Cristal Botella 600ml",
    marca: "Cristal",
    modelo: "Sin Gas",
    origen: "Colombia",
    costo: 800,
    precio: 1800,
    stock: 280,
    stockMinimo: 60,
    stockMaximo: 500,
    unidad: "botella",
    ubicacionFisica: "Nevera 3, bebidas frías",
    proveedor: "Postobón S.A.",
    vencimientoMeses: 12,
    pesoNeto: "600ml",
    descripcionLarga:
      "Agua natural de manantial, pureza y minerales balanceados. PET reciclable. Esencial para cualquier momento.",
    especificaciones: [
      { clave: "Origen", valor: "Manantial" },
      { clave: "Envase", valor: "PET reciclable" },
    ],
    imageKeywords: ["water", "bottled-water", "agua"],
    color: "bg-sky-300",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 7,
    nombre: "Café Juan Valdez 340g",
    marca: "Juan Valdez",
    modelo: "Clásico Suave",
    origen: "Colombia",
    costo: 18500,
    precio: 27900,
    stock: 70,
    stockMinimo: 15,
    stockMaximo: 140,
    unidad: "bolsa",
    ubicacionFisica: "Estante B2, café",
    proveedor: "Procafecol S.A.",
    vencimientoMeses: 12,
    pesoNeto: "340g",
    descripcionLarga:
      "Café 100% colombiano suave, tostado medio, molido. Notas a chocolate y panela. Cosechado por caficultores FNC.",
    especificaciones: [
      { clave: "Tostión", valor: "Medio" },
      { clave: "Molienda", valor: "Media" },
    ],
    imageKeywords: ["colombian-coffee", "juan-valdez", "coffee-bag"],
    color: "bg-amber-900",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 8,
    nombre: "Gatorade Uva 500ml",
    marca: "Gatorade",
    modelo: "Uva",
    origen: "Colombia",
    costo: 2900,
    precio: 4600,
    stock: 120,
    stockMinimo: 28,
    stockMaximo: 220,
    unidad: "botella",
    ubicacionFisica: "Nevera 3, bebidas frías",
    proveedor: "PepsiCo Alimentos",
    vencimientoMeses: 10,
    pesoNeto: "500ml",
    descripcionLarga:
      "Bebida isotónica de reposición de electrolitos. Sabor uva, ideal para entrenamientos y deportes de resistencia.",
    especificaciones: [
      { clave: "Electrolitos", valor: "Sodio + potasio" },
      { clave: "Formato", valor: "Botella 500ml" },
    ],
    imageKeywords: ["gatorade", "sports-drink", "isotonic"],
    color: "bg-purple-600",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 9,
    nombre: "Vino Tinto Reservado 750ml",
    marca: "Santa Rita",
    modelo: "Reservado Cabernet Sauvignon",
    origen: "Chile",
    costo: 19800,
    precio: 29900,
    stock: 45,
    stockMinimo: 10,
    stockMaximo: 90,
    unidad: "botella",
    ubicacionFisica: "Vitrina B3, vinos",
    proveedor: "Dislicores",
    vencimientoMeses: 36,
    pesoNeto: "750ml",
    tieneIVA: true,
    porcentajeIVA: 19,
    descripcionLarga:
      "Vino tinto Cabernet Sauvignon del Valle Central chileno. Notas a frutos rojos y cedro. Maridaje ideal con carnes rojas.",
    especificaciones: [
      { clave: "Variedad", valor: "Cabernet Sauvignon" },
      { clave: "Alcohol", valor: "13.5%" },
    ],
    imageKeywords: ["red-wine", "vino-tinto", "wine-bottle"],
    color: "bg-red-900",
    shape: "square",
  }),
];
